import React from "react";

import styled from "styled-components";

import { GeomT } from "models/model";

import { BaseCheckboxInput, ColorPicker } from "components/base";

interface FeatureRowRendererConfig {
  showCheckbox: boolean;
  showPropertyTitle: boolean;
}

interface FeatureRowRendererData {
  features: GeomT[];
  shapefileFeaturePropertyKey: string;
  updateFeatureProps: (
    featureId: string,
    oldFeature: GeomT,
    prop: string,
    value: string | number
  ) => void;
  selectedShapefile: {
    layer?: {
      style?: {
        color?: string;
        strokeColor?: string;
        thickness?: number;
        opacity?: number;
        lineStyle?: string;
        fillPattern?: string;
      };
    };
  };
  debouncedFeatureOpacityUpdate?: (
    featureId: string,
    oldFeature: GeomT,
    key: string,
    value: string | number
  ) => void;
  isReadOnly: boolean;
  onCheckFeature?: (value: boolean, feature: GeomT) => void;
}

interface FeatureRowRendererProps {
  style: React.CSSProperties;
  index: number;
  data: FeatureRowRendererData;
}

/**
 * Factory function that creates a feature row renderer for react-window virtualized lists.
 * This renderer is shared between Project and Organization shapefile components.
 *
 * @param config - Configuration options for the renderer
 * @param config.showCheckbox - Whether to display the checkbox for feature selection
 * @param config.showPropertyTitle - Whether to add title attribute to the property value
 * @returns A renderer function compatible with react-window's itemData pattern
 */
export function createFeatureRowRenderer(config: FeatureRowRendererConfig) {
  const FeatureRowRenderer = ({ style, index, data }: FeatureRowRendererProps) => {
    const {
      features,
      shapefileFeaturePropertyKey,
      updateFeatureProps,
      selectedShapefile,
      debouncedFeatureOpacityUpdate,
      isReadOnly,
      onCheckFeature
    } = data;

    const feature = features[index] as GeomT;

    // Null check for feature
    if (!feature) return null;

    const propertyValue = feature?.Properties?.[shapefileFeaturePropertyKey];

    return (
      // Named for the guided walkthrough, which points at a feature row to
      // explain the checkboxes. Every row carries it; `querySelector` takes the
      // first, which is the top row of an unscrolled list.
      <FeatureRow
        key={`${feature.shapefileGeomId}`}
        style={style}
        data-testid="shapefile-feature-row">
        {config.showCheckbox && onCheckFeature && (
          <BaseCheckboxInput
            value={feature.checked}
            onChange={(value) => onCheckFeature(value, feature)}
          />
        )}
        <ColorPicker
          isReadOnly={isReadOnly}
          onChange={(key, prop) =>
            updateFeatureProps(feature.shapefileGeomId, feature, key, prop)
          }
          onChangeOpacity={
            debouncedFeatureOpacityUpdate
              ? (key, value) =>
                  debouncedFeatureOpacityUpdate(
                    feature.shapefileGeomId,
                    feature,
                    key,
                    value
                  )
              : undefined
          }
          values={{
            color: feature.Color || selectedShapefile?.layer?.style?.color,
            strokeColor:
              feature.StrokeColor || selectedShapefile?.layer?.style?.strokeColor,
            thickness: feature.Thickness || selectedShapefile?.layer?.style?.thickness,
            opacity:
              (feature.Opacity || selectedShapefile?.layer?.style?.opacity || 1) * 100
          }}
        />
        <StyledPropertyValue
          {...(config.showPropertyTitle ? { title: propertyValue } : {})}>
          {propertyValue}
        </StyledPropertyValue>
      </FeatureRow>
    );
  };

  FeatureRowRenderer.displayName = "FeatureRowRenderer";
  return FeatureRowRenderer;
}

const FeatureRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;

  .trigger-container {
    display: block;
  }
`;

const StyledPropertyValue = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
  padding-right: 18px;
`;
